import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Minus, Phone, Plus, Sandwich, ShoppingBag, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, apiError, fmt } from "@/lib/api";

const FALLBACK_CATEGORIES = [
  { id: "fried", label: "Famous Fried Artichokes", subs: [] },
  { id: "coffee", label: "Coffee & Tea", subs: [] },
  { id: "breakfast", label: "Breakfast", subs: [] },
  { id: "lunch", label: "Lunch", subs: [] },
];

const PICKUP_TIMES = ["ASAP (15–20 min)", "In 30 minutes", "In 45 minutes", "In 1 hour"];

const BYO = {
  sizes: [
    { id: "whole", label: "Whole", price: 1550 },
    { id: "half", label: "Half", price: 1150 },
  ],
  breads: ["Ciabatta", "Sourdough", "Wheat", "Dutch Crunch"],
  meats: [
    { name: "Turkey", up: 0 }, { name: "Smoked Turkey", up: 0 }, { name: "Pastrami", up: 200 },
    { name: "Roast Beef", up: 200 }, { name: "Tuna", up: 0 }, { name: "Salami", up: 0 }, { name: "Ham", up: 0 },
  ],
  cheeses: ["American", "Cheddar", "Pepper Jack", "Swiss", "Monterey Jack", "Provolone"],
  veggies: ["Lettuce", "Tomatoes", "Onions", "Cucumbers", "Pickles", "Mayo", "Mustard"],
  extras: [
    { name: "Avocado", price: 175 }, { name: "Jalapeño", price: 75 }, { name: "Peperoncini", price: 75 },
    { name: "Garlic Aioli", price: 50 }, { name: "Honey Mustard", price: 50 }, { name: "Chipotle Mayo", price: 75 },
    { name: "Artichoke Hearts", price: 250 }, { name: "Sprouts", price: 50 },
  ],
};

const FRESH_BYO = { size: "whole", bread: "Dutch Crunch", meat: "Turkey", cheese: "Pepper Jack", veggies: ["Lettuce", "Tomatoes"], extras: [] };

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function ByoChip({ active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-all active:scale-95 ${
        active
          ? "border-artichoke bg-artichoke text-cream shadow-md shadow-artichoke/25"
          : "border-chalk/15 bg-white text-chalk/70 hover:border-artichoke/50 hover:text-artichoke"
      }`}
    >
      {children}
    </button>
  );
}

export default function Order() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subSel, setSubSel] = useState({});
  const [orderingOn, setOrderingOn] = useState(true);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("gac_cart") || "{}");
    } catch {
      return {};
    }
  });
  const [customs, setCustoms] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("gac_customs") || "[]");
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [byoOpen, setByoOpen] = useState(false);
  const [byo, setByo] = useState(FRESH_BYO);
  const [form, setForm] = useState({ name: "", phone: "", email: "", pickup_time: PICKUP_TIMES[0] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/menu")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Couldn't load the menu — try refreshing"));
    api
      .get("/categories")
      .then((r) => {
        setCategories(r.data);
        const sel = {};
        for (const c of r.data) {
          if (c.subs?.length) sel[c.id] = c.subs[0].id;
        }
        setSubSel(sel);
      })
      .catch(() => {});
    api
      .get("/settings/ordering")
      .then((r) => setOrderingOn(r.data.ordering_enabled))
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("gac_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem("gac_customs", JSON.stringify(customs));
  }, [customs]);

  const catList = categories.length ? categories : FALLBACK_CATEGORIES;

  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const cartEntries = Object.entries(cart).filter(([id, q]) => q > 0 && byId[id]);
  const cartCount = cartEntries.reduce((s, [, q]) => s + q, 0) + customs.length;
  const total =
    cartEntries.reduce((s, [id, q]) => s + byId[id].price_cents * q, 0) +
    customs.reduce((s, c) => s + c.unit_price_cents, 0);

  const byoPrice = useMemo(() => {
    const base = BYO.sizes.find((s) => s.id === byo.size).price;
    const meatUp = BYO.meats.find((m) => m.name === byo.meat)?.up || 0;
    const extrasSum = byo.extras.reduce((s, n) => s + (BYO.extras.find((e) => e.name === n)?.price || 0), 0);
    return base + meatUp + extrasSum;
  }, [byo]);

  if (!orderingOn) {
    return (
      <div data-testid="ordering-paused" className="flex min-h-screen flex-col items-center justify-center bg-sage px-4 text-center font-body">
        <img src="/logo.png" alt="" className="h-20 w-20 rounded-full shadow-lg" />
        <h1 className="mt-6 font-display text-3xl font-bold text-deep sm:text-4xl">
          Online ordering is paused right now
        </h1>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-chalk/60">
          The kitchen's heads-down cooking. Call us and we'll happily take your
          order over the phone — or check back in a bit.
        </p>
        <a
          data-testid="paused-call-button"
          href="tel:8316334259"
          className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-artichoke px-8 py-4 font-display text-lg font-semibold text-cream shadow-lg transition-colors hover:bg-deep"
        >
          <Phone className="h-5 w-5" /> Call (831) 633-4259
        </a>
        <a href="/" data-testid="paused-home-link" className="mt-4 text-sm font-bold text-artichoke hover:text-grape">
          ← Back to the site
        </a>
      </div>
    );
  }

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) =>
    setCart((c) => {
      const n = { ...c, [id]: (c[id] || 0) - 1 };
      if (n[id] <= 0) delete n[id];
      return n;
    });

  const toggleList = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const addByo = () => {
    const sizeLabel = byo.size === "whole" ? "Whole" : "Half";
    let build = `${byo.bread} / ${byo.meat} / ${byo.cheese}`;
    if (byo.veggies.length) build += ` / ${byo.veggies.join(", ")}`;
    if (byo.extras.length) build += ` / +${byo.extras.join(", ")}`;
    setCustoms((cs) => [
      ...cs,
      {
        key: `byo-${Date.now()}`,
        title: `BYO Sandwich (${sizeLabel})`,
        build,
        unit_price_cents: byoPrice,
        byo: { ...byo },
      },
    ]);
    setByo(FRESH_BYO);
    setByoOpen(false);
    toast.success("Your sandwich is in the order");
  };

  const removeCustom = (key) => setCustoms((cs) => cs.filter((c) => c.key !== key));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Add your name and phone number so we know it's you");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: [
          ...cartEntries.map(([item_id, qty]) => ({ item_id, qty })),
          ...customs.map((c) => ({ qty: 1, byo: c.byo })),
        ],
        name: form.name,
        phone: form.phone,
        email: form.email,
        pickup_time: form.pickup_time,
      });
      localStorage.removeItem("gac_cart");
      localStorage.removeItem("gac_customs");
      window.location.assign(data.checkout_url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "ordering_disabled") {
        setOrderingOn(false);
      } else if (e?.response?.status === 503) {
        toast.error("Online payment isn't live yet — call (831) 633-4259 to order");
      } else {
        toast.error(apiError(e));
      }
      setBusy(false);
    }
  };

  return (
    <div data-testid="order-page" className="min-h-screen bg-sage pb-32 font-body text-chalk">
      <header className="sticky top-0 z-40 border-b border-leafline/60 bg-cream/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <a href="/" data-testid="order-back-link" className="flex items-center gap-2 text-sm font-bold text-artichoke hover:text-grape">
            <ArrowLeft className="h-4 w-4" /> Back
          </a>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="" className="h-8 w-8 rounded-full" />
            <span className="font-display text-lg font-semibold text-deep">Order Pickup</span>
          </div>
          <a href="tel:8316334259" data-testid="order-call-link" className="flex items-center gap-1.5 text-sm font-bold text-artichoke hover:text-grape">
            <Phone className="h-4 w-4" /> <span className="hidden sm:inline">(831) 633-4259</span>
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pt-8">
        <h1 className="font-display text-3xl font-bold text-deep sm:text-4xl">What sounds good?</h1>
        <p className="mt-2 text-sm text-chalk/55">
          Order ahead, pay online, skip the line. Pickup at 11221 Merritt St, Castroville.
        </p>

        <section className="mt-10" data-testid="order-category-byo">
          <h2 className="border-b-2 border-chalk/10 pb-2 font-display text-xl font-bold text-artichoke">
            Build Your Own Sandwich
          </h2>
          <button
            data-testid="byo-open-button"
            onClick={() => setByoOpen(true)}
            className="mt-4 flex w-full flex-wrap items-center justify-between gap-4 rounded-3xl border-2 border-dashed border-artichoke/40 bg-cream p-5 text-left transition-all hover:border-artichoke hover:shadow-lg"
          >
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-artichoke/10">
                <Sandwich className="h-7 w-7 text-artichoke" />
              </span>
              <div>
                <p className="font-display text-lg font-bold text-deep">Your sandwich, your rules</p>
                <p className="text-sm text-chalk/55">
                  Pick bread, meat, cheese, veggies & extras — we build it fresh.
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-editorial text-base font-semibold text-crispy">Whole $15.50 · Half $11.50</p>
              <span className="mt-1 inline-block rounded-full bg-artichoke px-4 py-1.5 text-xs font-bold text-cream">
                Start Building →
              </span>
            </div>
          </button>
        </section>

        {catList.map((cat) => {
          const allCatItems = items.filter((i) => i.category === cat.id);
          if (allCatItems.length === 0) return null;
          const subs = [...(cat.subs || [])].sort((a, b) => a.sort - b.sort);
          const activeSub = subSel[cat.id];
          const visibleItems = subs.length
            ? allCatItems.filter((i) => i.subcategory === activeSub)
            : allCatItems;
          return (
            <section key={cat.id} className="mt-10" data-testid={`order-category-${cat.id}`}>
              <h2 className="border-b-2 border-chalk/10 pb-2 font-display text-xl font-bold text-artichoke">
                {cat.label}
              </h2>

              {subs.length > 0 && (
                <div className="mt-4 flex max-w-full gap-2 overflow-x-auto pb-1" data-testid={`sub-tabs-${cat.id}`}>
                  {subs.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`sub-chip-${s.id}`}
                      onClick={() => setSubSel({ ...subSel, [cat.id]: s.id })}
                      className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-all active:scale-95 ${
                        activeSub === s.id
                          ? "bg-grape text-cream shadow-md shadow-grape/25"
                          : "bg-white text-chalk/60 hover:text-grape"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              {visibleItems.length === 0 && subs.length > 0 ? (
                <p className="mt-6 text-sm italic text-chalk/45">Nothing here yet — check another shelf.</p>
              ) : (
                <div className="mt-5 grid gap-x-10 gap-y-3 md:grid-cols-2">
                  {visibleItems.map((it) => (
                    <div
                      key={it.id}
                      data-testid={`order-item-${it.id}`}
                      className="group flex items-center gap-3 rounded-2xl border border-transparent px-2 py-2 transition-colors hover:border-leafline hover:bg-cream"
                    >
                      {it.photo_path && (
                        <img
                          src={`${API}/files/${it.photo_path}`}
                          alt={it.title}
                          className="h-14 w-14 shrink-0 rounded-xl border border-leafline bg-white object-contain p-1"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <h3 className="font-semibold leading-snug transition-colors group-hover:text-grape">
                            {it.title}
                          </h3>
                          <span className="mx-1 hidden flex-1 border-b-2 border-dotted border-artichoke/30 sm:block" />
                        </div>
                        {it.desc && (
                          <p className="mt-0.5 truncate text-xs leading-relaxed text-chalk/55">{it.desc}</p>
                        )}
                      </div>
                      <span className="whitespace-nowrap font-editorial text-lg font-semibold text-crispy">
                        {fmt(it.price_cents)}
                      </span>
                      {cart[it.id] ? (
                        <div className="flex shrink-0 items-center gap-2.5 rounded-full bg-artichoke px-2 py-1.5 text-cream">
                          <button data-testid={`order-dec-${it.id}`} onClick={() => dec(it.id)} className="p-1 transition-transform active:scale-90" aria-label="Remove one">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="min-w-4 text-center font-display font-semibold">{cart[it.id]}</span>
                          <button data-testid={`order-inc-${it.id}`} onClick={() => add(it.id)} className="p-1 transition-transform active:scale-90" aria-label="Add one">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid={`order-add-${it.id}`}
                          onClick={() => add(it.id)}
                          className="shrink-0 rounded-full border-2 border-artichoke px-5 py-1.5 text-sm font-bold text-artichoke transition-all hover:bg-artichoke hover:text-cream active:scale-95"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </main>

      <AnimatePresence>
        {byoOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setByoOpen(false)}
              className="fixed inset-0 z-40 bg-chalk/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              data-testid="byo-modal"
              className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[92vh] w-full max-w-2xl flex-col rounded-t-[2rem] bg-sage shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-leafline px-6 py-4">
                <h2 className="font-display text-xl font-bold text-deep">Build Your Sandwich</h2>
                <button data-testid="byo-close-button" onClick={() => setByoOpen(false)} className="rounded-full p-2 hover:bg-cream" aria-label="Close builder">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5">
                <p className="mb-2.5 text-xs font-bold uppercase tracking-[0.2em] text-artichoke">1 · Size</p>
                <div className="flex flex-wrap gap-2">
                  {BYO.sizes.map((s) => (
                    <ByoChip key={s.id} testid={`byo-size-${s.id}`} active={byo.size === s.id} onClick={() => setByo({ ...byo, size: s.id })}>
                      {s.label} · {fmt(s.price)}
                    </ByoChip>
                  ))}
                </div>
                <p className="mb-2.5 mt-6 text-xs font-bold uppercase tracking-[0.2em] text-artichoke">2 · Bread</p>
                <div className="flex flex-wrap gap-2">
                  {BYO.breads.map((b) => (
                    <ByoChip key={b} testid={`byo-bread-${slug(b)}`} active={byo.bread === b} onClick={() => setByo({ ...byo, bread: b })}>
                      {b}
                    </ByoChip>
                  ))}
                </div>
                <p className="mb-2.5 mt-6 text-xs font-bold uppercase tracking-[0.2em] text-artichoke">3 · Meat</p>
                <div className="flex flex-wrap gap-2">
                  {BYO.meats.map((m) => (
                    <ByoChip key={m.name} testid={`byo-meat-${slug(m.name)}`} active={byo.meat === m.name} onClick={() => setByo({ ...byo, meat: m.name })}>
                      {m.name}{m.up ? ` +${fmt(m.up)}` : ""}
                    </ByoChip>
                  ))}
                </div>
                <p className="mb-2.5 mt-6 text-xs font-bold uppercase tracking-[0.2em] text-artichoke">4 · Cheese</p>
                <div className="flex flex-wrap gap-2">
                  {BYO.cheeses.map((c) => (
                    <ByoChip key={c} testid={`byo-cheese-${slug(c)}`} active={byo.cheese === c} onClick={() => setByo({ ...byo, cheese: c })}>
                      {c}
                    </ByoChip>
                  ))}
                </div>
                <p className="mb-2.5 mt-6 text-xs font-bold uppercase tracking-[0.2em] text-artichoke">5 · Veggies & condiments</p>
                <div className="flex flex-wrap gap-2">
                  {BYO.veggies.map((v) => (
                    <ByoChip key={v} testid={`byo-veggie-${slug(v)}`} active={byo.veggies.includes(v)} onClick={() => setByo({ ...byo, veggies: toggleList(byo.veggies, v) })}>
                      {v}
                    </ByoChip>
                  ))}
                </div>
                <p className="mb-2.5 mt-6 text-xs font-bold uppercase tracking-[0.2em] text-artichoke">6 · Extras</p>
                <div className="flex flex-wrap gap-2 pb-2">
                  {BYO.extras.map((e) => (
                    <ByoChip key={e.name} testid={`byo-extra-${slug(e.name)}`} active={byo.extras.includes(e.name)} onClick={() => setByo({ ...byo, extras: toggleList(byo.extras, e.name) })}>
                      {e.name} +{fmt(e.price)}
                    </ByoChip>
                  ))}
                </div>
              </div>

              <div className="border-t border-leafline bg-cream px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-xs text-chalk/55">
                      {byo.size === "whole" ? "Whole" : "Half"} · {byo.bread} · {byo.meat} · {byo.cheese}
                      {byo.extras.length > 0 && ` · +${byo.extras.join(", ")}`}
                    </p>
                    <p data-testid="byo-live-total" className="font-display text-2xl font-bold text-deep">{fmt(byoPrice)}</p>
                  </div>
                  <button
                    data-testid="byo-add-button"
                    onClick={addByo}
                    className="shrink-0 rounded-full bg-artichoke px-7 py-3.5 font-display text-base font-semibold text-cream shadow-lg shadow-artichoke/25 transition-all hover:bg-deep active:scale-95"
                  >
                    Add to Order
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cartCount > 0 && !cartOpen && (
          <motion.button
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            data-testid="cart-open-button"
            onClick={() => setCartOpen(true)}
            className="fixed bottom-5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full bg-chalk px-7 py-4 font-display text-base font-semibold text-cream shadow-2xl"
          >
            <ShoppingBag className="h-5 w-5 text-gold" />
            {cartCount} item{cartCount > 1 ? "s" : ""} · {fmt(total)}
            <span className="rounded-full bg-gold px-3 py-1 text-sm text-chalk">Checkout</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setCartOpen(false)}
              className="fixed inset-0 z-40 bg-chalk/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              data-testid="cart-drawer"
              className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-cream shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-leafline px-6 py-4">
                <h2 className="font-display text-xl font-bold text-deep">Your Order</h2>
                <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} className="rounded-full p-2 hover:bg-sage" aria-label="Close cart">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                {cartEntries.map(([id, qty]) => (
                  <div key={id} data-testid={`cart-item-${id}`} className="flex items-center gap-3 border-b border-dashed border-chalk/10 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{byId[id].title}</p>
                      <p className="text-xs text-chalk/50">{fmt(byId[id].price_cents)} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`cart-dec-${id}`} onClick={() => dec(id)} className="rounded-full border border-chalk/15 p-1.5" aria-label="Decrease">
                        {qty === 1 ? <Trash2 className="h-3.5 w-3.5 text-red-600" /> : <Minus className="h-3.5 w-3.5" />}
                      </button>
                      <span className="min-w-5 text-center text-sm font-bold">{qty}</span>
                      <button data-testid={`cart-inc-${id}`} onClick={() => add(id)} className="rounded-full border border-chalk/15 p-1.5" aria-label="Increase">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span className="w-16 text-right font-editorial font-semibold text-crispy">
                      {fmt(byId[id].price_cents * qty)}
                    </span>
                  </div>
                ))}

                {customs.map((c) => (
                  <div key={c.key} data-testid={`cart-custom-${c.key}`} className="flex items-start gap-3 border-b border-dashed border-chalk/10 py-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-artichoke/10">
                      <Sandwich className="h-4.5 w-4.5 h-5 w-5 text-artichoke" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{c.title}</p>
                      <p className="mt-0.5 text-xs leading-snug text-chalk/50">{c.build}</p>
                    </div>
                    <button data-testid={`cart-custom-remove-${c.key}`} onClick={() => removeCustom(c.key)} className="rounded-full border border-chalk/15 p-1.5" aria-label="Remove sandwich">
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </button>
                    <span className="w-16 text-right font-editorial font-semibold text-crispy">{fmt(c.unit_price_cents)}</span>
                  </div>
                ))}

                <div className="mt-6 space-y-3">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-artichoke">Pickup details</p>
                  <input
                    data-testid="checkout-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name *"
                    className="w-full rounded-2xl border-2 border-chalk/10 bg-white px-4 py-3 text-sm font-medium outline-none focus:border-artichoke"
                  />
                  <input
                    data-testid="checkout-phone-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone number *"
                    type="tel"
                    className="w-full rounded-2xl border-2 border-chalk/10 bg-white px-4 py-3 text-sm font-medium outline-none focus:border-artichoke"
                  />
                  <input
                    data-testid="checkout-email-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email (for receipt, optional)"
                    type="email"
                    className="w-full rounded-2xl border-2 border-chalk/10 bg-white px-4 py-3 text-sm font-medium outline-none focus:border-artichoke"
                  />
                  <select
                    data-testid="checkout-pickup-select"
                    value={form.pickup_time}
                    onChange={(e) => setForm({ ...form, pickup_time: e.target.value })}
                    className="w-full rounded-2xl border-2 border-chalk/10 bg-white px-4 py-3 text-sm font-medium outline-none focus:border-artichoke"
                  >
                    {PICKUP_TIMES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="border-t border-leafline px-6 py-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-bold uppercase tracking-wider text-chalk/50">Total</span>
                  <span data-testid="cart-total" className="font-display text-2xl font-bold text-deep">{fmt(total)}</span>
                </div>
                <button
                  data-testid="checkout-submit-button"
                  onClick={submit}
                  disabled={busy || cartCount === 0}
                  className="mt-4 w-full rounded-full bg-artichoke py-4 font-display text-lg font-semibold text-cream shadow-lg shadow-artichoke/25 transition-all hover:bg-deep disabled:opacity-60"
                >
                  {busy ? "Opening secure payment…" : `Pay ${fmt(total)} · Pickup`}
                </button>
                <p className="mt-2.5 text-center text-[11px] text-chalk/45">
                  Secure checkout powered by Clover. We start cooking when you pay.
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
